import { useState, useCallback, useEffect } from "react";
import DataTable from '../DataTable/DataTable';
import * as XLSX from "xlsx";
import { NotificationManager } from "react-notifications";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { convertDate } from "../AcitivityModal/assistingFunctions";
import { DateTime } from "luxon";
import "./requisitionReports.css";
import rtrReport from "../../apis/rtrReport/rtrReport";
import config from "../../Config.json";

const stylesSX = {
  odd: {
    background: '#F6F6F6 !important',
  },
  even: {
    background: '#ffffff !important',
  },
  backdrop: (theme) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  }),
  waitingText: {
    marginLeft: '5px',
  },
};

const RtrRports = (props) => {
    const {
        userIdType,
        userRecruitmentpartnerId,
        startSelectedDate,
        endSelectedDate,
        filter,
        exports,
        setTriggerRTR
    } = props;

    const [takeNumberOfResult, setTakeNumberOfResult] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [dataResult, setDataResult] = useState([]);
    const [totalResult, setTotalResult] = useState(0);
    const [isSearching, setIsSearching] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [prevStartDate, setPrevStartDate] = useState(null);
    const [prevEndDate, setPrevEndDate] = useState(null);
    const [prevFilter, setPrevFilter] = useState({});

    const columns = [
        {
            accessorKey: "candidateName",
            header: "Candidate Name",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "candidateEmail",
            header: "Candidate Email",
            width: "250px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "refId",
            header: "Reference ID",
            width: "150px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "rtrSendDate",
            header: "RTR Send Date",
            width: "150px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const rawDate = getValue();
                if (!rawDate) return "N/A";
                const date = new Date(rawDate);
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            },
        },
        {
            accessorKey: "approvalDate",
            header: "Approval Date",
            width: "150px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const rawDate = getValue();
                if (!rawDate) return "N/A";
                const date = new Date(rawDate);
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            },
        },
        {
            accessorKey: "rejectDate",
            header: "Reject Date",
            width: "150px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const rawDate = getValue();
                if (!rawDate) return "N/A";
                const date = new Date(rawDate);
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            },
        },
        {
            accessorKey: "rtrStatus",
            header: "RTR Status",
            width: "150px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const value = getValue();
                return value || "N/A";
            },
        },
        {
            accessorKey: "recruiterName",
            header: "Recruiter Name",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "teamLeaderName",
            header: "Team Leader",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const value = getValue();
                return value || "N/A";
            },
        },
        {
            accessorKey: "recruitmentLeaderName",
            header: "Recruitment Leader",
            width: "200px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const value = getValue();
                return value || "N/A";
            },
        },
    ];

    const performSearch = useCallback(async (
        userRecruitmentpartnerId,
        pageNumber,
        takeNumberOfResult,
        startSelectedDate,
        endSelectedDate,
        userId,
        additionalFilter
    ) => {
        try {
            setIsSearching(true);
            setDataResult([]);

            const payload = {
                formattedDatestart: convertDate(startSelectedDate),
                formattedDateend: convertDate(endSelectedDate),
                recruitmentpartnerId: userRecruitmentpartnerId,
                page: pageNumber,
                size: takeNumberOfResult,
                additionalFilter: {
                    teamId: additionalFilter?.teamId || [],
                    candidateName: additionalFilter?.candidateName || "",
                    candidateEmail: additionalFilter?.candidateEmail || "",
                    refId: additionalFilter?.refId || "",
                    rtrSendDate: {
                        from: additionalFilter?.rtrSendDate?.from || "",
                        to: additionalFilter?.rtrSendDate?.to || ""
                    },
                    approvalDate: {
                        from: additionalFilter?.approvalDate?.from || "",
                        to: additionalFilter?.approvalDate?.to || ""
                    },
                    rejectDate: {
                        from: additionalFilter?.rejectDate?.from || "",
                        to: additionalFilter?.rejectDate?.to || ""
                    },
                    rtrStatus: additionalFilter?.rtrStatus || ""
                }
            };

            const res = await rtrReport(payload);

            if (res?.data && res.data.length > 0 && res?.totalCount && res.totalCount.length > 0) {
                setDataResult(res.data);
                setTotalResult(res.totalCount[0].total);
            } else {
                setDataResult([]);
                setTotalResult(0);
            }
            setIsSearching(false);
        } catch (error) {
            console.error("Error fetching RTR report:", error);
            setDataResult([]);
            setTotalResult(0);
            setIsSearching(false);
            NotificationManager.error("Failed to load data", "Error", 10000);
        }
    }, []);

    const handlePageChange = (newPage, newPageSize) => {
        setPageNumber(newPage);
        if (newPageSize && newPageSize !== takeNumberOfResult) {
            setTakeNumberOfResult(newPageSize);
        }
    };

    const handlePageSizeChange = (newPageSize) => {
        setTakeNumberOfResult(newPageSize);
        setPageNumber(1);
    };

    useEffect(() => {
        const datesChanged = prevStartDate !== startSelectedDate || prevEndDate !== endSelectedDate;
        const filtersChanged = JSON.stringify(prevFilter) !== JSON.stringify(filter);

        if (datesChanged || filtersChanged) {
            setPageNumber(1);
            setPrevStartDate(startSelectedDate);
            setPrevEndDate(endSelectedDate);
            setPrevFilter({ ...filter });
        }
    }, [startSelectedDate, endSelectedDate, filter, prevStartDate, prevEndDate, prevFilter]);

    useEffect(() => {
        const start = convertDate(startSelectedDate);
        const end = convertDate(endSelectedDate);
        
        if (!start || !end || start > end) {
            setDataResult([]);
            setTotalResult(0);
            NotificationManager.error("Please select a valid date range", "Error", 10000);
        } else {
            setIsSearching(true);
            setDataResult([]);

            const timeoutId = setTimeout(() => {
                performSearch(
                    userRecruitmentpartnerId,
                    pageNumber,
                    takeNumberOfResult,
                    startSelectedDate,
                    endSelectedDate,
                    userIdType,
                    filter
                );
            }, 1000); 

            return () => clearTimeout(timeoutId);
        }
    }, [pageNumber, takeNumberOfResult, startSelectedDate, endSelectedDate, filter, userRecruitmentpartnerId, userIdType, performSearch]);

    const callApiToGenerateExcel = useCallback(async () => {
        setIsExporting(true);
        setTriggerRTR(false);
        const maxSize = config.MAX_SIZE;
        const end = Math.ceil(totalResult / maxSize);
        let pageStart = 1;
        const exportArr = [];
        
        try {
            for (let start = 0; start < end; start++) {
                const payload = {
                    formattedDatestart: convertDate(startSelectedDate),
                    formattedDateend: convertDate(endSelectedDate),
                    recruitmentpartnerId: userRecruitmentpartnerId,
                    isExport: true,
                    size: maxSize,
                    page: pageStart,
                    additionalFilter: {
                        teamId: filter?.teamId || [],
                        candidateName: filter?.candidateName || "",
                        candidateEmail: filter?.candidateEmail || "",
                        refId: filter?.refId || "",
                        rtrSendDate: {
                            from: filter?.rtrSendDate?.from || "",
                            to: filter?.rtrSendDate?.to || ""
                        },
                        approvalDate: {
                            from: filter?.approvalDate?.from || "",
                            to: filter?.approvalDate?.to || ""
                        },
                        rejectDate: {
                            from: filter?.rejectDate?.from || "",
                            to: filter?.rejectDate?.to || ""
                        },
                        rtrStatus: filter?.rtrStatus || ""
                    }
                };

                const res = await rtrReport(payload);

                if (res?.data && res.data.length > 0) {
                    res.data.forEach(({
                        candidateName,
                        candidateEmail,
                        refId,
                        rtrSendDate,
                        approvalDate,
                        rejectDate,
                        rtrStatus,
                        recruiterName,
                        teamLeaderName,
                        recruitmentLeaderName,
                        ...rest
                    }) => {
                        exportArr.push({
                            "Candidate Name": candidateName,
                            "Candidate Email": candidateEmail,
                            "Reference ID": refId,
                            "RTR Send Date": rtrSendDate ? DateTime.fromISO(rtrSendDate).toFormat("MM/dd/yyyy").toString() : "N/A",
                            "Approval Date": approvalDate ? DateTime.fromISO(approvalDate).toFormat("MM/dd/yyyy").toString() : "N/A",
                            "Reject Date": rejectDate ? DateTime.fromISO(rejectDate).toFormat("MM/dd/yyyy").toString() : "N/A",
                            "Rtr Status": rtrStatus || "N/A",
                            "Recruiter Name": recruiterName,
                            "Team Leader Name": teamLeaderName || "N/A",
                            "Recruitment Leader Name": recruitmentLeaderName || "N/A",
                            ...rest
                        });
                    });
                }
                pageStart = pageStart + maxSize;
            }

            if (exportArr.length === 0) {
                NotificationManager.error("No data available", "Error", 10000);
                setIsExporting(false);
            } else {
                const ws = XLSX.utils.json_to_sheet(exportArr);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "RTR Report");
                XLSX.writeFile(wb, "RTR_report.xlsx");
                setIsExporting(false);
            }
        } catch (error) {
            console.error("Error exporting:", error);
            NotificationManager.error("Failed to export", "Error", 10000);
            setIsExporting(false);
        }
    }, [startSelectedDate, endSelectedDate, filter, setTriggerRTR, totalResult]);

    useEffect(() => {
        if (exports) {
            callApiToGenerateExcel();
        }
    }, [exports, callApiToGenerateExcel]);

    return (
        <>
            <Backdrop sx={stylesSX.backdrop} open={isExporting}>
                <CircularProgress color="inherit" />
                <Typography sx={stylesSX.waitingText}>Please wait...</Typography>
            </Backdrop>

            <DataTable
                data={dataResult}
                columns={columns}
                isLoading={isSearching}
                totalRecords={totalResult}
                currentPage={pageNumber}
                pageSize={takeNumberOfResult}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableFiltering={false}
                enablePagination={true}
                enableSorting={false}
                pageSizeOptions={[10, 20, 50, 100]}
                isServerSide={true}
                className="kendo_reports"
                noDataText="No results found !"
            />
        </>
    );
};

export default RtrRports;
